import React, { RefObject, useEffect, useRef } from "react";
import styled, { css } from "styled-components";
import { ONBOARDING_PHONE_GUTTER } from "features/onboarding/common/onboardingUi";
import { useTelegramWebAppBottomPadding } from "utils/hooks/useTelegramWebApp";

const PHONE = 560;

/**
 * While the bar is fixed (phones), the onboarding page keeps room for it at
 * its bottom (--onboarding-action-bar), so nothing ends up beneath it.
 */
export const useActionBarSpace = (ref: RefObject<HTMLElement | null>) => {
  useEffect(() => {
    const bar = ref.current;
    if (!bar || typeof ResizeObserver === "undefined") return;
    const root = document.documentElement;
    const sync = () => {
      const fixed = getComputedStyle(bar).position === "fixed";
      root.style.setProperty(
        "--onboarding-action-bar",
        fixed ? `${Math.ceil(bar.getBoundingClientRect().height)}px` : "0px"
      );
    };
    const observer = new ResizeObserver(sync);
    observer.observe(bar);
    window.addEventListener("resize", sync);
    sync();
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", sync);
      root.style.removeProperty("--onboarding-action-bar");
    };
  }, [ref]);
};

/**
 * The bottom inset a bar fixed to the screen's bottom keeps clear: the
 * browser's, or in the Telegram Mini App Telegram's own (its WebView often
 * reports none to CSS).
 */
export const useFixedBarBottomInset = (): string => {
  const telegramBottom = useTelegramWebAppBottomPadding();
  return `max(env(safe-area-inset-bottom, 0px), ${telegramBottom}px)`;
};

/**
 * The step's main action and its alternatives. In place on wider screens;
 * on phones pinned to the bottom of the screen, as in a native app, so the
 * action never scrolls away.
 */
const PhoneActionBar = ({
  children,
  className,
  busy,
  phoneOnly,
}: React.PropsWithChildren<{
  className?: string;
  /** Wider screens keep the children's own layout (no bar box at all). */
  phoneOnly?: boolean;
  /** Shown but inactive while the step loads, so nothing shifts later. */
  busy?: boolean;
}>) => {
  const ref = useRef<HTMLDivElement>(null);
  useActionBarSpace(ref);
  const bottomInset = useFixedBarBottomInset();
  return (
    <Bar
      ref={ref}
      className={className}
      data-onboarding-bar=""
      aria-busy={busy || undefined}
      $busy={busy}
      $phoneOnly={phoneOnly}
      $bottomInset={bottomInset}
    >
      {children}
    </Bar>
  );
};

export default PhoneActionBar;

/* The bar's box: in place on wider screens (unless phoneOnly), fixed to the
   bottom edge on phones. */
const barBox = css`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  text-align: center;
  .ant-btn {
    width: 100%;
  }
`;
const Bar = styled.div<{
  $busy?: boolean;
  $phoneOnly?: boolean;
  $bottomInset: string;
}>`
  ${(p) => p.$busy && "pointer-events: none; opacity: 0.5;"}
  transition: opacity 0.2s ease;
  /* phoneOnly: no box at all on wider screens; the children keep their own
     layout there. */
  ${(p) => (p.$phoneOnly ? "display: contents;" : barBox)}
  @media (max-width: ${PHONE}px) {
    ${barBox};
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 20;
    margin: 0 !important;
    .ant-btn {
      margin: 0 !important;
    }
    padding: 12px ${ONBOARDING_PHONE_GUTTER}px
      calc(8px + ${(p) => p.$bottomInset});
    border-top: 1px solid ${(p) => p.theme.colors.cardBorderColor};
    background: ${(p) => p.theme.colors.cardBgColor};
  }
`;
